import { Download, Loader2, Minus, Plus } from 'lucide-react';
import { useCallback, useRef, useState, type MouseEvent } from 'react';

/**
 * Process-diagram viewer with inline zoom + pan. Shared between the
 * scenario detail modal (`ScenarioDetailModal`) and the Presentation
 * detail page (`PresentationDetailPage`). Mirrors the legacy
 * `wf-audit/src/slices/scenario/components/scenarioThumb/
 * ProcessDiagram.tsx`:
 *
 *   • Tall container shows the Fusion-captured PNG.
 *   • Top-right zoom controls: `−` / `100 %` / `+`. Reset doubles
 *     as click-on-the-percent.
 *   • When `scale > 1` the image is draggable: mousedown on the
 *     canvas, drag, mouseup. Pointer changes to grab / grabbing.
 *   • Bottom-left caption shows `Last Updated: …` formatted with
 *     date + time + timezone abbreviation (e.g. `GMT-7`).
 *   • Bottom-right download button (`<a download>`) saves the PNG
 *     locally with a `<scenario>-<fusionId>.png` filename.
 *
 * Container height stays fixed (`heightClass` prop) regardless of
 * whether the screenshot has loaded — without this the empty state
 * height would mismatch the loaded-image height and the surrounding
 * layout would jump when the image arrived. Operators (2026-05-25)
 * reported the modal jumping when the bytes hit; same applies on
 * the presentation page.
 */
export interface ScenarioProcessDiagramProps {
  /** Captured PNG URL. `null` shows the "not captured" placeholder
   *  in the same-sized container so layout doesn't shift. */
  link: string | null;
  /** ISO timestamp surfaced in the bottom-left "Last Updated" caption.
   *  Pass `null` to hide the caption. */
  modifiedAt: string | null;
  /** Scenario name, used to derive the local download filename. */
  scenarioName: string | null;
  /** Scenario fusion id, used as a filename suffix so two scenarios
   *  with the same name don't collide in the operator's Downloads. */
  fusionId: string | null;
  /** Tailwind height class for the viewer container. Defaults to
   *  `h-[60vh] min-h-[320px]` (the modal value). Callers that need
   *  to fit inside a constrained parent (e.g. the Presentation
   *  detail's right column) can pass `h-full min-h-[300px]`. */
  heightClass?: string;
  /** `true` while the parent's detail fetch is still in flight. The
   *  diagram shows a spinner placeholder rather than the "not
   *  captured" message — operators (2026-05-26) reported the empty
   *  state flashing before the fetch returned a `screenshotLink`.
   *  When the fetch completes and `link` is still null, the
   *  component falls through to the "not captured" message. */
  isLoading?: boolean;
}

const DIAGRAM_ZOOM_MIN = 0.25;
const DIAGRAM_ZOOM_MAX = 4;
const DIAGRAM_ZOOM_STEP = 0.25;

export function ScenarioProcessDiagram({
  link,
  modifiedAt,
  scenarioName,
  fusionId,
  heightClass = 'h-[60vh] min-h-[320px]',
  isLoading = false,
}: ScenarioProcessDiagramProps): JSX.Element {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Tracks whether the `<img>` has finished loading from the network.
  // Reset whenever `link` changes so navigating between scenarios
  // doesn't briefly flash the previous diagram. The spinner overlay
  // sits over the image while this is `true` so the operator sees
  // motion until the bytes arrive (typically a few hundred ms for
  // the cached PNG, up to ~2 s for a cold S3 fetch).
  const [imageLoading, setImageLoading] = useState<boolean>(Boolean(link));
  const lastLinkRef = useRef<string | null>(link);
  if (lastLinkRef.current !== link) {
    lastLinkRef.current = link;
    setImageLoading(Boolean(link));
  }
  // Drag state — `dragStart` records the mouse + offset at the
  // beginning of the drag so we can compute the new offset from the
  // mouse delta on every move event. `null` when not dragging.
  const dragStart = useRef<{
    mouseX: number;
    mouseY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);

  const zoomIn = useCallback(() => {
    setScale((s) => Math.min(DIAGRAM_ZOOM_MAX, +(s + DIAGRAM_ZOOM_STEP).toFixed(2)));
  }, []);
  const zoomOut = useCallback(() => {
    setScale((s) => {
      const next = Math.max(DIAGRAM_ZOOM_MIN, +(s - DIAGRAM_ZOOM_STEP).toFixed(2));
      // Once we're back at 1.0 or smaller, clear any pan offset so the
      // image re-centres — otherwise a previously-panned image stays
      // off-centre when the operator zooms back out.
      if (next <= 1) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);
  const resetZoom = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const onMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (scale <= 1) return; // Pan only makes sense when zoomed in.
      if (e.button !== 0) return; // Primary button only.
      dragStart.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        offsetX: offset.x,
        offsetY: offset.y,
      };
      setDragging(true);
    },
    [scale, offset.x, offset.y],
  );
  const onMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.mouseX;
    const dy = e.clientY - dragStart.current.mouseY;
    setOffset({
      x: dragStart.current.offsetX + dx,
      y: dragStart.current.offsetY + dy,
    });
  }, []);
  const onMouseUp = useCallback(() => {
    dragStart.current = null;
    setDragging(false);
  }, []);

  const canDrag = scale > 1;
  return (
    <div className="relative overflow-hidden rounded-md border border-foreground/15 bg-white">
      <div
        className={`relative w-full select-none overflow-hidden ${heightClass}`}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        style={{ cursor: link && canDrag ? (dragging ? 'grabbing' : 'grab') : 'default' }}
      >
        {link ? (
          <>
            <img
              src={link}
              alt="Scenario process diagram"
              draggable={false}
              className="absolute inset-0 m-auto block max-h-full max-w-full object-contain"
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                transformOrigin: 'center center',
                transition: dragging ? 'none' : 'transform 120ms ease-out',
                imageRendering: scale > 1.5 ? 'crisp-edges' : 'auto',
                opacity: imageLoading ? 0 : 1,
              }}
              onLoad={() => setImageLoading(false)}
              onError={(e) => {
                setImageLoading(false);
                e.currentTarget.style.display = 'none';
              }}
            />
            {imageLoading ? (
              <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : null}
          </>
        ) : isLoading ? (
          // Detail fetch still in flight — show a neutral spinner so
          // we don't claim the diagram is missing before we know for
          // sure (operator feedback: "Process diagram not captured"
          // was flashing during the initial load).
          <div className="absolute inset-0 flex items-center justify-center text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          // Fetch settled with no screenshot URL → genuinely no
          // diagram captured for this scenario.
          <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
            Process diagram not captured yet for this scenario.
          </div>
        )}
      </div>

      {/* Zoom controls + caption + download only render with an image.
          Container height stays fixed regardless so swapping the empty
          state for the loaded diagram doesn't shift surrounding layout. */}
      {link ? (
        <>
          <div className="absolute right-3 top-3 inline-flex items-center gap-0.5 rounded-md border border-black/10 bg-white/95 px-1 py-0.5 text-gray-700 shadow-sm">
            <button
              type="button"
              onClick={zoomOut}
              disabled={scale <= DIAGRAM_ZOOM_MIN}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-700 hover:bg-black/10 disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Zoom out"
              title="Zoom out"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={resetZoom}
              className="rounded px-1.5 text-xs font-medium tabular-nums text-gray-800 hover:bg-black/10"
              aria-label={`Zoom: ${Math.round(scale * 100)}% — click to reset`}
              title="Reset zoom"
            >
              {Math.round(scale * 100)}%
            </button>
            <button
              type="button"
              onClick={zoomIn}
              disabled={scale >= DIAGRAM_ZOOM_MAX}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-700 hover:bg-black/10 disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Zoom in"
              title="Zoom in"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>

          {modifiedAt ? (
            <div className="absolute bottom-2 left-3 rounded bg-white/85 px-2 py-0.5 text-[11px] text-gray-600 shadow-sm">
              Last Updated: {formatDiagramTimestamp(modifiedAt)}
            </div>
          ) : null}
          <a
            href={link}
            download={diagramFilename(scenarioName, fusionId)}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-2 right-3 inline-flex items-center gap-1 rounded-md border border-black/10 bg-white/95 px-2 py-1 text-xs text-gray-700 shadow-sm hover:bg-white hover:text-gray-900"
            aria-label="Download process diagram"
            title="Download process diagram"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Download className="h-3.5 w-3.5" />
          </a>
        </>
      ) : null}
    </div>
  );
}

/**
 * Local filename the browser uses when the operator clicks the
 * diagram's Download button. Slugged scenario name + fusion id keeps
 * the filename readable + collision-free across scenarios with the
 * same name. Falls back to `scenario-{id}.png` when the row has no
 * name yet.
 */
function diagramFilename(name: string | null, fusionId: string | null): string {
  const id = fusionId ?? 'unknown';
  const safe = (name ?? '')
    .replace(/[^\w.-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return safe ? `${safe}-${id}.png` : `scenario-${id}.png`;
}

/**
 * Verbose timestamp for the process-diagram footer. Matches the
 * legacy `wf-audit` format: `MM/DD/YYYY, HH:MM:SS AM/PM, GMT±H`.
 * Uses the local timezone — operators care about the moment they
 * read the diagram in their own clock, not UTC.
 */
function formatDiagramTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number): string => n.toString().padStart(2, '0');
  const hours24 = d.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const ampm = hours24 < 12 ? 'AM' : 'PM';
  // Timezone offset in hours. `getTimezoneOffset` returns minutes
  // *behind* UTC (positive for west of UTC) — flip the sign so GMT-7
  // reads as "ahead of UTC by 7", matching the legacy display.
  const tzMinutes = -d.getTimezoneOffset();
  const tzHours = tzMinutes / 60;
  const tzSign = tzHours >= 0 ? '+' : '-';
  const tzAbs = Math.abs(tzHours);
  const tzLabel = Number.isInteger(tzAbs)
    ? `GMT${tzSign}${tzAbs}`
    : `GMT${tzSign}${tzAbs.toFixed(1)}`;
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}, ${pad(hours12)}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${ampm}, ${tzLabel}`;
}
